import { IconPlus } from '@tabler/icons-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { CanWrite, ViewerNotice } from '../components/CanWrite'
import { Dialog } from '../components/Dialog'
import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  FieldErrorText,
  LoadingState,
} from '../components/States'
import type { MerchantView } from '../lib/apiTypes'
import { formatDateWib, formatDuration } from '../lib/format'
import { describeError, hasFieldError } from '../lib/describeError'
import {
  merchantSettingsHint,
  merchantSettingsTemplate,
  providerBJBVA,
} from '../lib/providerSettings'
import { useCredentials, useMerchants, useCreateMerchant, useUpdateMerchant } from '../lib/hooks'
import { rowStatusLabel, rowStatusTone } from '../lib/status'

/**
 * Daftar merchant: unit usaha yang menerima pembayaran. Merchant terhubung ke
 * satu kredensial provider, dan pengaturan masa berlaku pembayaran bisa diubah
 * per merchant. Kanalnya (QRIS atau Virtual Account) mengikuti provider
 * kredensial yang dipilih.
 */
export function MerchantsPage() {
  const merchants = useMerchants()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<MerchantView | null>(null)

  return (
    <>
      <div className="page-head">
        <div className="page-head__text">
          <h1>Merchant</h1>
          <p className="muted">
            Merchant menentukan kredensial provider yang dipakai, kode merchant di sisi
            bank, dan masa berlaku pembayaran. Client hanya bisa memakai merchant yang
            didaftarkan untuknya.
          </p>
        </div>
        <CanWrite>
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <IconPlus size={18} stroke={1.75} aria-hidden="true" />
            Buat merchant
          </button>
        </CanWrite>
      </div>

      {merchants.isPending ? (
        <LoadingState what="daftar merchant" />
      ) : merchants.isError ? (
        <ErrorState
          title="Gagal memuat daftar merchant"
          error={merchants.error}
          onRetry={() => void merchants.refetch()}
          retrying={merchants.isFetching}
        />
      ) : (merchants.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Belum ada merchant"
          body="Merchant dibuat setelah kredensial provider terpasang. Tanpa merchant, client tidak punya tujuan transaksi."
          action={
            <Link className="btn" to="/credentials">
              Buka halaman Kredensial
            </Link>
          }
        />
      ) : (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Tabel merchant, geser horizontal dengan tombol panah">
          <table className="table">
            <caption className="visually-hidden">
              Daftar merchant dengan status, kredensial, kode bank, dan masa berlaku pembayaran
            </caption>
            <thead>
              <tr>
                <th scope="col">Merchant</th>
                <th scope="col">Status</th>
                <th scope="col">Kredensial</th>
                <th scope="col">Kode bank</th>
                <th scope="col" className="is-num">
                  Masa berlaku
                </th>
                <th scope="col">Tindakan</th>
              </tr>
            </thead>
            <tbody>
              {merchants.data?.data.map((row) => (
                <tr key={row.id}>
                  <td>
                    <span className="fact__value">{row.name}</span>
                    <br />
                    <span className="caption mono">{row.slug}</span>
                  </td>
                  <td>
                    <Badge tone={rowStatusTone(row.status)}>{rowStatusLabel(row.status)}</Badge>
                  </td>
                  <td>
                    <span className="mono">{row.credential}</span>
                    <br />
                    <span className="caption">
                      {row.provider} di lingkungan {row.environment}
                    </span>
                  </td>
                  <td className="mono">
                    {row.merchant_code || 'belum diisi'}
                    <br />
                    <span className="caption">
                      {row.nmid ? `NMID ${row.nmid}` : 'NMID belum diisi'}
                    </span>
                  </td>
                  <td className="is-num">
                    {formatDuration(row.default_expiry_seconds * 1000)}
                    <br />
                    <span className="caption">
                      {formatDuration(row.min_expiry_seconds * 1000)} sampai{' '}
                      {formatDuration(row.max_expiry_seconds * 1000)}
                    </span>
                  </td>
                  <td className="is-actions">
                    <CanWrite>
                      <button type="button" className="btn btn--sm" onClick={() => setEditing(row)}>
                        Ubah
                      </button>
                    </CanWrite>
                    <span className="visually-hidden">Dibuat {formatDateWib(row.created_at)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!merchants.isPending && !merchants.isError ? <ViewerNotice /> : null}

      {creating ? <MerchantDialog onClose={() => setCreating(false)} /> : null}
      {editing ? (
        <MerchantDialog merchant={editing} onClose={() => setEditing(null)} />
      ) : null}
    </>
  )
}

/**
 * Dialog buat dan ubah merchant. Bidang kode bank dibiarkan kosong bila bank
 * belum menerbitkannya; kolom settings diisi JSON yang divalidasi server.
 */
function MerchantDialog({
  merchant,
  onClose,
}: {
  merchant?: MerchantView
  onClose: () => void
}) {
  const credentials = useCredentials()
  const create = useCreateMerchant()
  const update = useUpdateMerchant(merchant?.id ?? '')
  const [slug, setSlug] = useState(merchant?.slug ?? '')
  const [name, setName] = useState(merchant?.name ?? '')
  const [credential, setCredential] = useState(merchant?.credential ?? '')
  const [status, setStatus] = useState(merchant?.status ?? 'ACTIVE')
  const [merchantCode, setMerchantCode] = useState(merchant?.merchant_code ?? '')
  const [bankMerchantId, setBankMerchantId] = useState(merchant?.bank_merchant_id ?? '')
  const [subMerchantId, setSubMerchantId] = useState(merchant?.sub_merchant_id ?? '')
  const [storeId, setStoreId] = useState(merchant?.store_id ?? '')
  const [terminalId, setTerminalId] = useState(merchant?.terminal_id ?? '')
  const [nmid, setNmid] = useState(merchant?.nmid ?? '')
  const [defaultExpiry, setDefaultExpiry] = useState(String(merchant?.default_expiry_seconds ?? 900))
  const [minExpiry, setMinExpiry] = useState(String(merchant?.min_expiry_seconds ?? 60))
  const [maxExpiry, setMaxExpiry] = useState(String(merchant?.max_expiry_seconds ?? 86400))
  const [settings, setSettings] = useState(
    merchant?.settings ? JSON.stringify(merchant.settings, null, 2) : '',
  )
  const [settingsTouched, setSettingsTouched] = useState(false)
  const [settingsError, setSettingsError] = useState('')

  const busy = create.isPending || update.isPending
  const error = merchant ? update.error : create.error
  // Provider merchant ditentukan kredensialnya, bukan dipilih terpisah. Untuk
  // merchant baru nilainya baru diketahui setelah kredensial dipilih.
  const selectedProvider =
    credentials.data?.data.find((item) => item.slug === credential)?.provider ??
    merchant?.provider ??
    ''

  function changeCredential(next: string) {
    setCredential(next)
    // Contoh settings berbeda per kanal dan server menolak kunci yang tidak
    // dikenal, jadi isian yang belum disentuh operator ikut berganti.
    if (!settingsTouched) {
      setSettings(
        merchantSettingsTemplate(
          credentials.data?.data.find((item) => item.slug === next)?.provider ?? '',
        ),
      )
    }
  }

  function submit() {
    setSettingsError('')
    let parsedSettings: unknown = undefined
    if (settings.trim()) {
      try {
        parsedSettings = JSON.parse(settings)
      } catch {
        setSettingsError('Isi settings bukan JSON yang sah. Perbaiki tanda kutip dan koma.')
        return
      }
    }

    if (merchant) {
      update.mutate(
        {
          name: name.trim(),
          status: status as MerchantView['status'],
          merchant_code: merchantCode.trim(),
          bank_merchant_id: bankMerchantId.trim(),
          sub_merchant_id: subMerchantId.trim(),
          store_id: storeId.trim(),
          terminal_id: terminalId.trim(),
          default_expiry_seconds: Number(defaultExpiry),
          min_expiry_seconds: Number(minExpiry),
          max_expiry_seconds: Number(maxExpiry),
          ...(parsedSettings === undefined ? {} : { settings: parsedSettings }),
        },
        { onSuccess: onClose },
      )
      return
    }

    create.mutate(
      {
        slug: slug.trim(),
        name: name.trim(),
        credential,
        merchant_code: merchantCode.trim(),
        bank_merchant_id: bankMerchantId.trim(),
        sub_merchant_id: subMerchantId.trim(),
        store_id: storeId.trim(),
        terminal_id: terminalId.trim(),
        default_expiry_seconds: Number(defaultExpiry),
        min_expiry_seconds: Number(minExpiry),
        max_expiry_seconds: Number(maxExpiry),
        settings: parsedSettings ?? {},
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog
      title={merchant ? `Ubah merchant ${merchant.slug}` : 'Buat merchant'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Batal
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={busy || name.trim() === '' || (!merchant && (slug.trim() === '' || credential === ''))}
          >
            {busy ? 'Menyimpan...' : merchant ? 'Simpan perubahan' : 'Simpan merchant'}
          </button>
        </>
      }
    >
      {error ? <Alert tone="danger">{describeError(error)}</Alert> : null}

      <div className="form-grid">
        {!merchant ? (
          <>
            <div className="field">
              <label className="field__label" htmlFor="m-slug">
                Slug
              </label>
              <input
                id="m-slug"
                className="input mono"
                value={slug}
                onChange={(event) => setSlug(event.target.value)}
                aria-invalid={hasFieldError(error, 'slug')}
              />
              <FieldErrorText error={error} field="slug" />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="m-credential">
                Kredensial
              </label>
              <select
                id="m-credential"
                disabled={credentials.isPending || credentials.isError}
                className="select"
                value={credential}
                onChange={(event) => changeCredential(event.target.value)}
                aria-invalid={hasFieldError(error, 'credential')}
              >
                <option value="">{credentials.isPending ? 'Memuat kredensial...' : !credentials.data?.data.length ? 'Belum ada kredensial' : 'Pilih kredensial'}</option>
                {(credentials.data?.data ?? []).map((item) => (
                  <option key={item.id} value={item.slug}>
                    {item.slug} ({item.provider}, {item.environment})
                  </option>
                ))}
              </select>
              <FieldErrorText error={error} field="credential" />
              {credentials.isError ? <ErrorState title="Gagal memuat pilihan kredensial" error={credentials.error} onRetry={() => void credentials.refetch()} /> : null}
            </div>
          </>
        ) : null}

        <div className="field">
          <label className="field__label" htmlFor="m-name">
            Nama
          </label>
          <input
            id="m-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            aria-invalid={hasFieldError(error, 'name')}
          />
          <FieldErrorText error={error} field="name" />
        </div>

        {merchant ? (
          <div className="field">
            <label className="field__label" htmlFor="m-status">
              Status
            </label>
            <select
              id="m-status"
              className="select"
              value={status}
              onChange={(event) => setStatus(event.target.value as MerchantView['status'])}
            >
              <option value="ACTIVE">Aktif</option>
              <option value="DISABLED">Nonaktif</option>
            </select>
            <FieldErrorText error={error} field="status" />
          </div>
        ) : null}

        <div className="field">
          <label className="field__label" htmlFor="m-code">
            Kode merchant
          </label>
          <input
            id="m-code"
            className="input mono"
            value={merchantCode}
            onChange={(event) => setMerchantCode(event.target.value)}
          />
          {selectedProvider === providerBJBVA ? (
            <span className="field__hint">
              Kode bank ini dipakai kanal QRIS. Merchant Virtual Account dikenali dari
              partner_service_id di settings, jadi biarkan kosong bila tidak dipakai.
            </span>
          ) : null}
          <FieldErrorText error={error} field="merchant_code" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-bank-merchant">
            ID merchant bank
          </label>
          <input
            id="m-bank-merchant"
            className="input mono"
            value={bankMerchantId}
            onChange={(event) => setBankMerchantId(event.target.value)}
          />
          <FieldErrorText error={error} field="bank_merchant_id" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-sub-merchant">
            ID sub merchant
          </label>
          <input
            id="m-sub-merchant"
            className="input mono"
            value={subMerchantId}
            onChange={(event) => setSubMerchantId(event.target.value)}
          />
          <FieldErrorText error={error} field="sub_merchant_id" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-store">
            ID store
          </label>
          <input
            id="m-store"
            className="input mono"
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
          />
          <FieldErrorText error={error} field="store_id" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-terminal">
            ID terminal
          </label>
          <input
            id="m-terminal"
            className="input mono"
            value={terminalId}
            onChange={(event) => setTerminalId(event.target.value)}
          />
          <FieldErrorText error={error} field="terminal_id" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-nmid">
            NMID
          </label>
          <input
            id="m-nmid"
            className="input mono"
            value={nmid}
            onChange={(event) => setNmid(event.target.value)}
          />
          <FieldErrorText error={error} field="nmid" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-default-expiry">
            Masa berlaku bawaan (detik)
          </label>
          <input
            id="m-default-expiry"
            className="input num"
            inputMode="numeric"
            value={defaultExpiry}
            onChange={(event) => setDefaultExpiry(event.target.value)}
          />
          <FieldErrorText error={error} field="default_expiry_seconds" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-min-expiry">
            Masa berlaku minimum (detik)
          </label>
          <input
            id="m-min-expiry"
            className="input num"
            inputMode="numeric"
            value={minExpiry}
            onChange={(event) => setMinExpiry(event.target.value)}
          />
          <FieldErrorText error={error} field="min_expiry_seconds" />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="m-max-expiry">
            Masa berlaku maksimum (detik)
          </label>
          <input
            id="m-max-expiry"
            className="input num"
            inputMode="numeric"
            value={maxExpiry}
            onChange={(event) => setMaxExpiry(event.target.value)}
          />
          <FieldErrorText error={error} field="max_expiry_seconds" />
        </div>

        <div className="field form-grid--full">
          <label className="field__label" htmlFor="m-settings">
            Settings JSON
          </label>
          <textarea
            id="m-settings"
            className="textarea"
            value={settings}
            onChange={(event) => {
              setSettingsTouched(true)
              setSettings(event.target.value)
            }}
            aria-describedby="m-settings-hint"
            aria-invalid={Boolean(settingsError) || hasFieldError(error, 'settings')}
          />
          <span className="field__hint" id="m-settings-hint">
            {merchantSettingsHint(selectedProvider)}
            {settings.trim() === '' ? ' Kosongkan untuk memakai default server.' : ''}
          </span>
          {settingsError ? <p className="field__error">{settingsError}</p> : null}
          <FieldErrorText error={error} field="settings" />
        </div>
      </div>
    </Dialog>
  )
}
